type ExplorerResult = {
  name: string
  address: string
  transaction: string // NOTE: this is only in use for InsiderTransactions and only for Ethereum in it at the time.
}

export function getBlockExplorer(
  chain: string,
  address: string
): ExplorerResult {
  switch (chain.toLowerCase()) {
    case 'ethereum':
      return {
        name: 'Etherscan',
        address: `https://etherscan.io/address/${address}`,
        transaction: `https://etherscan.io/tx/${address}`,
      }
    case 'polygon':
      return {
        name: 'Polygonscan',
        address: `https://polygonscan.com/address/${address}`,
        transaction: `https://polygonscan.com/tx/${address}`,
      }
    case 'optimism':
      return {
        name: 'OP Mainnet Explorer',
        address: `https://optimistic.etherscan.io/address/${address}`,
        transaction: `https://optimistic.etherscan.io/tx/${address}`,
      }
    case 'avalanche':
      return {
        name: 'Snowtrace',
        address: `https://snowtrace.io/address/${address}`,
        transaction: `https://snowtrace.io/tx/${address}`,
      }
    case 'arbitrum':
      return {
        name: 'Arbiscan',
        address: `https://arbiscan.io/address/${address}`,
        transaction: `https://arbiscan.io/tx/${address}`,
      }
    case 'binance-smart-chain':
      return {
        name: 'Bscscan',
        address: `https://bscscan.com/address/${address}`,
        transaction: `https://bscscan.com/tx/${address}`,
      }
    case 'starknet':
      return {
        name: 'Starkscan',
        address: `https://starkscan.co/contract/${address}`,
        transaction: `https://starkscan.co/tx/${address}`,
      }
    case 'fantom':
      return {
        name: 'FTMScan',
        address: `https://ftmscan.com/address/${address}`,
        transaction: `https://ftmscan.com/tx/${address}`,
      }
    case 'gnosis-chain':
      return {
        name: 'Gnosisscan',
        address: `https://gnosisscan.io/address/${address}`,
        transaction: `https://gnosisscan.io/tx/${address}`,
      }
    case 'base':
      return {
        name: 'Basescan',
        address: `https://basescan.org/address/${address}`,
        transaction: `https://basescan.org/tx/${address}`,
      }
    case 'blast':
      return {
        name: 'Blastscan',
        address: `https://blastscan.io/address/${address}`,
        transaction: `https://blastscan.io/tx/${address}`,
      }
    case 'ronin':
      return {
        name: 'Ronin Block Explorer',
        address: `https://app.roninchain.com/address/${address}`,
        transaction: `https://app.roninchain.com/tx/${address}`,
      }
    case 'celo':
      return {
        name: 'CeloScan',
        address: `https://celoscan.io/address/${address}`,
        transaction: `https://celoscan.io/tx/${address}`,
      }
    case 'tron':
      return {
        name: 'TRONSCAN',
        address: `https://tronscan.org/#/address/${address}`,
        transaction: `https://tronscan.org/#/searchResult/${address}?type=tokens&contract`, // Not in use, but could be in use for InsiderTransactions if it had the chain.
      }
    case 'zksync-era':
      return {
        name: 'zkSync Era Block Explorer',
        address: `https://era.zksync.network/address/${address}`,
        transaction: `https://era.zksync.network/tx/${address}`, // Not in use, but could be in use for InsiderTransactions if it had the chain.
      }
    case 'aptos':
      return {
        name: 'Aptoscan',
        address: `https://aptoscan.com/account/${address}`,
        transaction: `https://aptoscan.com/transaction/${address}`,
      }
    case 'terra':
      return {
        name: 'Chainscope',
        address: `https://chainsco.pe/terra2/address/${address}`,
        transaction: `https://chainsco.pe/terra2/tx/${address}`,
      }
    case 'the-open-network':
      return {
        name: 'TONScan',
        address: `https://tonscan.org/address/${address}`,
        transaction: `https://tonscan.org/tx/${address}`,
      }
    case 'immutablezkevm':
      return {
        name: 'Immutable explorer',
        address: `https://explorer.immutable.com/address/${address}`,
        transaction: `https://explorer.immutable.com/tx/${address}`,
      }
    case 'injective':
      return {
        name: 'Injective Explorer',
        address: `https://explorer.injective.network/account/${address}`,
        transaction: `https://explorer.injective.network/transaction/${address}`,
      }
    case 'manta':
      return {
        name: 'Manta Pacific Explorer',
        address: `https://pacific-explorer.manta.network/address/${address}`,
        transaction: `https://pacific-explorer.manta.network/tx/${address}`,
      }
  }

  throw new Error(`Chain "${chain}" not supported.`)
}

export function getMEVAnalyticsExplorer(
  chain: string,
  address: string
): ExplorerResult {
  switch (chain.toLowerCase()) {
    case 'ethereum':
      return {
        name: 'EighenPhi',
        address: `https://eigenphi.io/mev/${chain}/${address}`,
        transaction: `https://eigenphi.io/mev/eigentx/${address}`,
      }
  }

  throw new Error(`Chain "${chain}" not supported.`)
}

// This defines the order or items for search context menu
export const availableSearchItems = [
  'google',
  'twitter',
  'github',
  'debank',
  'zapper',
] as const

export function getSearchItem(
  type: (typeof availableSearchItems)[number],
  address: string
): Omit<ExplorerResult, 'transaction'> {
  switch (type.toLowerCase()) {
    case 'google':
      return {
        name: 'Google',
        address: `https://www.google.com/search?q=${address}`,
      }
    case 'twitter':
      return {
        name: 'Twitter',
        address: `https://twitter.com/search?q=${address}`,
      }
    case 'github':
      return {
        name: 'GitHub',
        address: `https://www.github.com/search?q=${address}&type=code`,
      }
    case 'debank':
      return {
        name: 'DeBank',
        address: `https://www.debank.com/profile/${address}`,
      }
    case 'zapper':
      return {
        name: 'Zapper',
        address: `https://zapper.xyz/account/${address}`,
      }
  }

  throw new Error(`SearchItem "${type}" not supported.`)
}
